import type { ParsedPsd, Template, TemplateLayer } from "../types";

function extractLogicalName(urlOrName: string): string {
  if (!urlOrName) return urlOrName;
  if (!/^https?:/i.test(urlOrName)) return urlOrName;
  try {
    const u = new URL(urlOrName);
    const last = u.pathname.split("/").filter(Boolean).pop();
    return last ?? urlOrName;
  } catch {
    return urlOrName.split("/").pop() ?? urlOrName;
  }
}

async function fetchAsBlob(url: string): Promise<Blob> {
  const res = await fetch(url, { credentials: "omit" });
  if (!res.ok) throw new Error(`Falha ao baixar ${url} (${res.status})`);
  return res.blob();
}

function blobToImage(blob: Blob): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(blob);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image decode failed"));
    img.src = url;
  });
}

function imageToCanvas(img: HTMLImageElement): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d");
  if (ctx) ctx.drawImage(img, 0, 0);
  return canvas;
}

function makeThumbnail(canvas: HTMLCanvasElement, max = 96): string {
  const ratio = canvas.width / canvas.height || 1;
  let w = max;
  let h = max;
  if (ratio >= 1) h = Math.max(1, Math.round(max / ratio));
  else w = Math.max(1, Math.round(max * ratio));
  const thumb = document.createElement("canvas");
  thumb.width = w;
  thumb.height = h;
  const ctx = thumb.getContext("2d");
  if (ctx) {
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(canvas, 0, 0, w, h);
  }
  return thumb.toDataURL("image/png");
}

export interface HydratedTemplate {
  parsed: ParsedPsd;
  previewBlob: Blob | null;
}

/**
 * Convert a backend template record (with Supabase URLs) into editor state.
 * Image layer URLs are reduced to logical filenames; blobs/canvases are
 * fetched and cached so the preview renders.
 */
export async function hydrateBackendTemplate(
  configJson: Template,
): Promise<HydratedTemplate> {
  const urlByLogical = new Map<string, string>();
  const layers: TemplateLayer[] = configJson.layers.map((layer) => {
    if (layer.type !== "image") return layer;
    const logical = extractLogicalName(layer.imageUrl);
    if (/^https?:/i.test(layer.imageUrl) && !urlByLogical.has(logical)) {
      urlByLogical.set(logical, layer.imageUrl);
    }
    return { ...layer, imageUrl: logical };
  });

  const previewLogical = configJson.preview
    ? extractLogicalName(configJson.preview)
    : undefined;
  const previewUrl =
    configJson.preview && /^https?:/i.test(configJson.preview)
      ? configJson.preview
      : null;

  const canvases = new Map<string, HTMLCanvasElement>();
  const images = new Map<string, Blob>();
  const thumbnails = new Map<string, string>();

  await Promise.all(
    Array.from(urlByLogical.entries()).map(async ([logical, url]) => {
      try {
        const blob = await fetchAsBlob(url);
        const img = await blobToImage(blob);
        const canvas = imageToCanvas(img);
        canvases.set(logical, canvas);
        images.set(logical, blob);
        thumbnails.set(logical, makeThumbnail(canvas));
      } catch {
        // Silently skip; layer will not render but state stays consistent.
      }
    }),
  );

  let previewBlob: Blob | null = null;
  if (previewUrl) {
    try {
      previewBlob = await fetchAsBlob(previewUrl);
    } catch {
      previewBlob = null;
    }
  }

  const template: Template = {
    ...configJson,
    preview: previewLogical,
    layers,
  };

  return {
    parsed: {
      template,
      images,
      canvases,
      thumbnails,
    },
    previewBlob,
  };
}
